import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, ImageOff, Layers } from "lucide-react";
import api from "@/lib/api";
import { BlurImage } from "@/components/site/BlurImage";
import { useContent } from "@/context/ContentContext";

export const CategoryBadge = ({ category }) => (
  <span className="font-mono-tech text-[0.6rem] uppercase tracking-[0.2em] bg-sky-500/10 text-sky-400 border border-sky-500/30 px-2.5 py-1">
    {category || "Project"}
  </span>
);

const MotionLink = motion(Link);

const ProjectCard = ({ project, index }) => {
  const thumb = project.images?.[0] || project.doc_pages?.[0];
  return (
    <MotionLink
      to={`/projects/${project.id}`}
      data-testid={`project-card-${index}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      className="block group text-left bg-[#0d1420] border border-slate-800 rounded-lg hover:border-sky-500/50 hover:-translate-y-1 transition-[transform,border-color] duration-200 overflow-hidden"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-900">
        {thumb ? (
          <BlurImage
            path={thumb}
            w={640}
            fill
            alt={project.title}
            className="object-cover group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-700">
            <ImageOff className="h-10 w-10" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a111f] to-transparent opacity-60" />
        {project.images?.length > 1 && (
          <span className="absolute top-3 right-3 flex items-center gap-1 font-mono-tech text-[0.6rem] bg-[#050a14]/80 text-slate-300 px-2 py-1 border border-slate-700">
            <Layers className="h-3 w-3" /> {project.images.length}
          </span>
        )}
      </div>
      <div className="p-6">
        <CategoryBadge category={project.category} />
        <h3 className="mt-4 font-cond font-bold text-2xl uppercase tracking-tight text-slate-100 group-hover:text-sky-400 transition-colors">
          {project.title}
        </h3>
        <p className="mt-2 text-sm text-slate-400 line-clamp-2 leading-relaxed">
          {project.description}
        </p>
      </div>
    </MotionLink>
  );
};

export const ProjectGallery = ({ images, title }) => {
  const [idx, setIdx] = useState(0);
  if (!images?.length) return null;
  return (
    <div data-testid="project-gallery" className="relative aspect-video bg-[#0a111f] border border-slate-800 rounded-lg overflow-hidden flex items-center justify-center">
      <BlurImage key={images[idx]} path={images[idx]} w={1280} fill eager alt={`${title} ${idx + 1}`} className="object-contain" testid="gallery-image" />
      {images.length > 1 && (
        <>
          <button
            data-testid="gallery-prev"
            onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#050a14]/80 border border-slate-700 p-2 hover:border-sky-400 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-slate-100" />
          </button>
          <button
            data-testid="gallery-next"
            onClick={() => setIdx((i) => (i + 1) % images.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#050a14]/80 border border-slate-700 p-2 hover:border-sky-400 transition-colors"
          >
            <ChevronRight className="h-5 w-5 text-slate-100" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                data-testid={`gallery-dot-${i}`}
                onClick={() => setIdx(i)}
                className={`h-1.5 w-6 transition-colors ${i === idx ? "bg-sky-400" : "bg-slate-600"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const ProjectGrid = ({ projects }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
    {projects.map((p, i) => (
      <ProjectCard key={p.id} project={p} index={i} />
    ))}
  </div>
);

export const Projects = () => {
  const { content } = useContent();
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/projects", { params: { featured: true } })
      .then((res) => setProjects(res.data))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || projects.length === 0) return null;

  return (
    <section id="projects" data-testid="projects-section" className="py-24 border-t border-slate-800/60">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 mb-12">
          <h2 className="font-cond font-bold uppercase tracking-tight text-3xl lg:text-4xl text-slate-100">
            {content.projects?.heading || "Projects"}
          </h2>
          <Link
            to="/projects"
            data-testid="view-all-projects"
            className="group inline-flex items-center gap-2 text-sm text-sky-400 hover:text-sky-300 transition-colors whitespace-nowrap"
          >
            View all
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <ProjectGrid projects={projects} />
      </div>
    </section>
  );
};
